import { useMemo, useState } from 'react';
import {
  Box, TextField, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Button, Stack, Typography, ToggleButton, ToggleButtonGroup, Tooltip, alpha, useTheme,
} from '@mui/material';
import { Search, FileDownload, FilterList, ViewHeadline, ViewStream, ViewAgenda, ViewWeek } from '@mui/icons-material';
import {
  DataGrid,
  GridColDef,
  DataGridProps,
  GridValidRowModel,
  GridDensity,
  useGridApiRef,
  GridPreferencePanelsValue,
} from '@mui/x-data-grid';
import { SERIAL_NO_FIELD, withSerialNo } from './gridColumns';
import ResponsiveGridContainer from './ResponsiveGridContainer';
import { useResponsiveGrid } from '../../hooks/useResponsiveGrid';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { adminDataGridSx } from './adminDataGridSx';
import AdminGridEmpty from './AdminGridEmpty';

export interface StatusFilterOption {
  value: string;
  label: string;
}

interface EnhancedDataGridProps extends Omit<DataGridProps, 'rows'> {
  rows: GridValidRowModel[];
  exportFileName?: string;
  searchPlaceholder?: string;
  statusField?: string;
  statusOptions?: StatusFilterOption[];
  toolbarExtra?: React.ReactNode;
  showSerialNo?: boolean;
  disableLocalSearch?: boolean;
  emptyTitle?: string;
  emptySubtitle?: string;
}

function rowSearchText(row: GridValidRowModel, columns: GridColDef[]): string {
  return columns
    .filter((c) => c.field && c.field !== 'actions' && c.field !== SERIAL_NO_FIELD)
    .map((col) => {
      try {
        const val = col.valueGetter
          ? (col.valueGetter as (v: unknown, r: typeof row) => unknown)(row[col.field!], row)
          : row[col.field!];
        return cellText(val);
      } catch {
        return cellText(row[col.field!]);
      }
    })
    .join(' ')
    .toLowerCase();
}

function exportCellValue(row: Record<string, unknown>, col: GridColDef): string {
  try {
    const raw = col.valueGetter
      ? (col.valueGetter as (v: unknown, r: typeof row) => unknown)(row[col.field!], row)
      : row[col.field!];
    return cellText(raw);
  } catch {
    return cellText(row[col.field!]);
  }
}

function cellText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.firstName) return [obj.firstName, obj.lastName].filter(Boolean).join(' ');
    if (obj.name) return String(obj.name);
    if (obj.total != null) return String(obj.total);
  }
  return String(value);
}

function exportRowsToCsv(rows: Record<string, unknown>[], columns: GridColDef[], fileName: string) {
  const exportCols = columns.filter((c) => c.field && c.field !== 'actions' && c.field !== SERIAL_NO_FIELD);
  const header = ['"S.No"', ...exportCols.map((c) => `"${(c.headerName || c.field).replace(/"/g, '""')}"`)].join(',');
  const body = rows.map((row, index) =>
    [`"${index + 1}"`, ...exportCols.map((col) => `"${exportCellValue(row, col).replace(/"/g, '""')}"`)].join(','),
  );
  const csv = [header, ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${fileName}-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export default function EnhancedDataGrid({
  rows,
  columns,
  exportFileName = 'export',
  searchPlaceholder = 'Search records…',
  statusField,
  statusOptions = [],
  toolbarExtra,
  showSerialNo = true,
  disableLocalSearch = false,
  emptyTitle: emptyTitleProp,
  emptySubtitle: emptySubtitleProp,
  columnVisibilityModel: externalVisibility,
  initialState,
  ...gridProps
}: EnhancedDataGridProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const apiRef = useGridApiRef();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [density, setDensity] = useState<GridDensity>('standard');

  const gridColumns = useMemo(() => {
    const mapped = (columns as GridColDef[]).map((col) => ({
      ...col,
      headerName: col.headerName ? translateUiText(col.headerName, t) : col.headerName,
    }));
    return withSerialNo(mapped, showSerialNo).map((col) =>
      col.field === SERIAL_NO_FIELD
        ? { ...col, headerName: translateUiText('S.No', t) }
        : col,
    );
  }, [columns, showSerialNo, t]);

  const {
    columnVisibilityModel,
    density: responsiveDensity,
    pageSize,
    responsiveColumns,
    isMobile,
  } = useResponsiveGrid(gridColumns, externalVisibility);

  const activeDensity = isMobile ? responsiveDensity : density;
  const rowHeight = activeDensity === 'compact' ? 40 : activeDensity === 'comfortable' ? 64 : 52;
  const columnHeaderHeight = activeDensity === 'compact' ? 42 : 48;

  const filteredRows = useMemo(() => {
    if (disableLocalSearch) return rows;
    let result = rows;
    if (statusField && statusFilter) {
      result = result.filter((row) => String(row[statusField]) === statusFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((row) => rowSearchText(row, gridColumns).includes(q));
    }
    return result;
  }, [rows, gridColumns, search, statusField, statusFilter, disableLocalSearch]);

  const {
    slots: userSlots,
    sx: userSx,
    getRowId: userGetRowId,
    ...restGridProps
  } = gridProps;

  const emptyTitle = translateUiText(emptyTitleProp || 'No data found', t);
  const emptySubtitle = translateUiText(
    emptySubtitleProp
      || ((disableLocalSearch ? false : !!search.trim()) || !!statusFilter
        ? 'Try a different search or status filter'
        : 'Nothing to show yet'),
    t,
  );
  const EmptyOverlay = () => <AdminGridEmpty title={emptyTitle} subtitle={emptySubtitle} />;
  const gridBodyHeight = filteredRows.length === 0 ? 420 : undefined;

  return (
    <Box>
      <Box
        sx={{
          px: { xs: 1.75, sm: 2.25 },
          py: 1.35,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} flex={1} minWidth={0}>
          {!disableLocalSearch && (
            <TextField
              size="small"
              placeholder={translateUiText(searchPlaceholder, t)}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: { sm: 220 },
                flex: 1,
                maxWidth: { md: 280 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: alpha(theme.palette.text.primary, 0.03),
                  minHeight: 38,
                },
              }}
            />
          )}
          {statusField && statusOptions.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{translateUiText('Status', t)}</InputLabel>
              <Select
                label={translateUiText('Status', t)}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                startAdornment={<FilterList fontSize="small" sx={{ ml: 1, mr: 0.5, color: 'action.active' }} />}
              >
                <MenuItem value="">{translateUiText('All statuses', t)}</MenuItem>
                {statusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{translateUiText(opt.label, t)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600 }}>
            {translateUiText(
              disableLocalSearch
                ? `${filteredRows.length} rows`
                : `${filteredRows.length} of ${rows.length}`,
              t,
            )}
          </Typography>

          {!isMobile && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={density}
              onChange={(_, v: GridDensity | null) => { if (v) setDensity(v); }}
              sx={{
                '& .MuiToggleButton-root': {
                  px: 0.85,
                  py: 0.45,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  },
                },
              }}
            >
              <ToggleButton value="compact">
                <Tooltip title={translateUiText('Compact', t)}><ViewHeadline fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="standard">
                <Tooltip title={translateUiText('Comfortable', t)}><ViewStream fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="comfortable">
                <Tooltip title={translateUiText('Spacious', t)}><ViewAgenda fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <Tooltip title={translateUiText('Columns', t)}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewWeek fontSize="small" />}
              onClick={() => apiRef.current?.showPreferences(GridPreferencePanelsValue.columns)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary', borderColor: 'divider' }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>{translateUiText('Columns', t)}</Box>
            </Button>
          </Tooltip>

          {toolbarExtra}

          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownload fontSize="small" />}
            onClick={() => exportRowsToCsv(filteredRows, gridColumns, exportFileName)}
            disabled={!filteredRows.length}
            sx={{ whiteSpace: 'nowrap', textTransform: 'none', fontWeight: 600 }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{translateUiText('Export', t)}</Box>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{translateUiText('CSV', t)}</Box>
          </Button>
        </Stack>
      </Box>

      <ResponsiveGridContainer minHeight={gridBodyHeight ?? { xs: 360, sm: 420 }}>
        <DataGrid
          apiRef={apiRef}
          rows={filteredRows}
          getRowId={userGetRowId ?? ((row) => String(row.id ?? row._id))}
          pageSizeOptions={[10, 15, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu={false}
          columnHeaderHeight={columnHeaderHeight}
          rowHeight={rowHeight}
          {...restGridProps}
          columns={responsiveColumns}
          density={activeDensity}
          columnVisibilityModel={columnVisibilityModel}
          initialState={{ pagination: { paginationModel: { pageSize } }, ...initialState }}
          slots={{
            ...userSlots,
            noRowsOverlay: userSlots?.noRowsOverlay ?? EmptyOverlay,
            noResultsOverlay: userSlots?.noResultsOverlay ?? EmptyOverlay,
          }}
          sx={[
            adminDataGridSx(theme),
            gridBodyHeight
              ? {
                  minHeight: gridBodyHeight,
                  height: gridBodyHeight,
                  '& .MuiDataGrid-overlayWrapper': { minHeight: gridBodyHeight - columnHeaderHeight - 52 },
                }
              : {},
            ...(Array.isArray(userSx) ? userSx : userSx ? [userSx] : []),
          ]}
        />
      </ResponsiveGridContainer>
    </Box>
  );
}
